/**
 * Debug logging utility
 *
 * Logging is disabled by default on public shares.
 * Enable with:
 * - Keyboard: Ctrl+Shift+D (toggles debug mode)
 * - URL param: ?debug=true
 * - Console: window.__RESCRIPT_DEBUG__ = true
 */

// Check if we're on a public share (has share= in URL)
const isPublicShare = () => window.location.search.includes('share=');

// Check URL param
const hasDebugParam = () => new URLSearchParams(window.location.search).get('debug') === 'true';

// Initialize debug state
let debugEnabled = hasDebugParam() || (!isPublicShare() && process.env.NODE_ENV === 'development');

// Expose globally for console access
(window as any).__RESCRIPT_DEBUG__ = debugEnabled;

// Watch for changes to the global flag
Object.defineProperty(window, '__RESCRIPT_DEBUG__', {
  get: () => debugEnabled,
  set: (value: boolean) => {
    debugEnabled = value;
    console.log(`[DEBUG] Debug logging ${value ? 'ENABLED' : 'DISABLED'}`);
  }
});

// Keyboard shortcut: Ctrl+Shift+D
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      (window as any).__RESCRIPT_DEBUG__ = !debugEnabled;
    }
  });
}

/**
 * Debug log - only outputs when debug mode is enabled
 */
export const debugLog = (prefix: string, ...args: any[]) => {
  if (debugEnabled) {
    console.log(`[${prefix}]`, ...args);
  }
};

/**
 * Debug warn - only outputs when debug mode is enabled
 */
export const debugWarn = (prefix: string, ...args: any[]) => {
  if (debugEnabled) {
    console.warn(`[${prefix}]`, ...args);
  }
};

/**
 * Debug error - always outputs (errors should always be visible)
 */
export const debugError = (prefix: string, ...args: any[]) => {
  console.error(`[${prefix}]`, ...args);
};

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = () => debugEnabled;

export default { debugLog, debugWarn, debugError, isDebugEnabled };
